import { StringContext } from "../../core/StringContext";
import { StringData } from "../../core/StringData";
import { StringModule } from "../../core/StringModule";
import { StringObject } from "../../objects/StringObject";
import { styleTxn } from "../../utils/style-txn";

type CircularOrientation = "radial" | "tangent";
type CircularRenderer = "dom" | "svg";

type CircularConfig = {
  radius: number;
  startAngle: number;
  sweep: number;
  direction: 1 | -1;
  rotateGlyphs: boolean;
  orientation: CircularOrientation;
  inside: boolean;
  spinSpeed: number;
  spacing: number;
  keepUpright: boolean;
  renderer: CircularRenderer;
};

type CircularState = {
  chars: HTMLElement[];
  widths: number[];
  heights: number[];
  outwardOffsets: number[];
  centers: number[];
  pathLength: number;
  hostSize: number;
  rotation: number;
  lastAppliedRotation: number;
  measured: boolean;
  // Signatures of the config the current glyph metrics and placement were
  // built from. Re-measuring means a canvas measureText plus a getComputedStyle
  // per glyph, so it happens only once the inputs that feed it actually move —
  // never on the bare fact that another frame went by.
  appliedMeasureKey: string;
  appliedLayoutKey: string;
  needsRemeasure: boolean;
  needsLayout: boolean;
  svg: SVGSVGElement | null;
  svgPath: SVGPathElement | null;
  svgText: SVGTextElement | null;
  svgTextPath: SVGTextPathElement | null;
  svgPathId: string;
};

const circularState = new WeakMap<StringObject, CircularState>();

const getState = (object: StringObject): CircularState => {
  let state = circularState.get(object);

  if (!state) {
    state = {
      chars: [],
      widths: [],
      heights: [],
      outwardOffsets: [],
      centers: [],
      pathLength: 0,
      hostSize: 0,
      rotation: 0,
      lastAppliedRotation: Number.NaN,
      measured: false,
      appliedMeasureKey: "",
      appliedLayoutKey: "",
      needsRemeasure: false,
      needsLayout: false,
      svg: null,
      svgPath: null,
      svgText: null,
      svgTextPath: null,
      svgPathId: `string-circular-path-${Math.random().toString(36).slice(2)}`,
    };

    circularState.set(object, state);
  }

  return state;
};

// Only these feed glyph metrics and path length; a change to any of them
// invalidates the measurement.
const buildMeasureKey = (config: CircularConfig) =>
  `${config.radius}|${config.spacing}|${config.sweep}|${config.rotateGlyphs}|${config.orientation}|${config.inside}|${config.renderer}`;

// These reposition already-measured glyphs, so they need a re-apply but not a
// re-measure.
const buildLayoutKey = (config: CircularConfig) =>
  `${config.startAngle}|${config.direction}|${config.keepUpright}`;

const round3 = (value: number) => Math.round(value * 1000) / 1000;
const degToRad = (value: number) => (value * Math.PI) / 180;

const normalizeAngle = (value: number) => {
  let normalized = value % 360;
  if (normalized > 180) normalized -= 360;
  if (normalized <= -180) normalized += 360;
  return normalized;
};

const normalizeRotation = (value: number) => {
  let normalized = value % 360;
  if (normalized < 0) normalized += 360;
  return normalized;
};

const parsePx = (value: string | null | undefined, fallback = 0) => {
  if (!value || value === "normal") return fallback;
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const measurementCanvas = typeof document !== "undefined" ? document.createElement("canvas") : null;

const measurementContext = measurementCanvas?.getContext("2d") ?? null;
const SVG_NS = "http://www.w3.org/2000/svg";

const buildCanvasFont = (style: CSSStyleDeclaration) => {
  const shorthand = style.font?.trim();
  if (shorthand) return shorthand;

  const fontStyle = style.fontStyle || "normal";
  const fontWeight = style.fontWeight || "400";
  const fontSize = style.fontSize || "16px";
  const fontFamily = style.fontFamily || "sans-serif";

  return `${fontStyle} ${fontWeight} ${fontSize} ${fontFamily}`;
};

const measureGlyph = (char: HTMLElement) => {
  const text = char.textContent && char.textContent.length > 0 ? char.textContent : " ";
  const style = getComputedStyle(char);
  const fontSize = parsePx(style.fontSize, 16);
  const letterSpacing = parsePx(style.letterSpacing, 0);
  const kerningOffset = parsePx(style.marginRight, 0);

  let width = 0;
  let ascent = 0;
  let descent = 0;

  if (measurementContext) {
    measurementContext.font = buildCanvasFont(style);

    const ctxAny = measurementContext as CanvasRenderingContext2D & {
      fontKerning?: "auto" | "normal" | "none";
      letterSpacing?: string;
    };

    if ("fontKerning" in ctxAny) {
      const kerning =
        style.fontKerning === "normal" || style.fontKerning === "none" ? style.fontKerning : "auto";
      ctxAny.fontKerning = kerning;
    }

    const metrics = measurementContext.measureText(text);

    width = metrics.width;
    ascent = metrics.actualBoundingBoxAscent || fontSize * 0.78;
    descent = metrics.actualBoundingBoxDescent || fontSize * 0.22;
  } else {
    width = text.trim().length === 0 ? fontSize * 0.33 : fontSize * 0.55;
    ascent = fontSize * 0.78;
    descent = fontSize * 0.22;
  }

  width = Math.max(0, width);
  const height = Math.max(1, ascent + descent);
  const outwardOffset = (ascent - descent) / 2;
  const finalWidth = Math.max(
    width + letterSpacing + kerningOffset,
    text.trim().length === 0 ? fontSize * 0.18 : 0,
  );

  return {
    width: finalWidth,
    height,
    outwardOffset,
  };
};

const getGlyphElement = (char: HTMLElement): HTMLElement => {
  const existing = char.firstElementChild;
  if (
    existing instanceof HTMLElement &&
    existing.getAttribute("data-string-circular-glyph") === ""
  ) {
    return existing;
  }

  const glyph = document.createElement("span");
  glyph.setAttribute("data-string-circular-glyph", "");

  while (char.firstChild) {
    glyph.appendChild(char.firstChild);
  }

  char.appendChild(glyph);
  return glyph;
};

const getOriginalText = (host: HTMLElement) =>
  (host.getAttribute("string-split-original") ?? host.textContent ?? "").replace(/\s+/g, " ").trim();

const describeCircularPath = (cx: number, cy: number, radius: number, config: CircularConfig) => {
  const direction = config.direction;
  const sweep = Math.abs(config.sweep);
  const sweepFlag = direction === 1 ? 1 : 0;
  const point = (angle: number) => {
    const radians = degToRad(angle);
    return {
      x: round3(cx + Math.cos(radians) * radius),
      y: round3(cy + Math.sin(radians) * radius),
    };
  };

  if (Math.abs(sweep - 360) < 0.001) {
    const start = point(config.startAngle);
    const mid = point(config.startAngle + 180 * direction);
    const end = point(config.startAngle + 359.999 * direction);

    return [
      `M ${start.x} ${start.y}`,
      `A ${radius} ${radius} 0 1 ${sweepFlag} ${mid.x} ${mid.y}`,
      `A ${radius} ${radius} 0 1 ${sweepFlag} ${end.x} ${end.y}`,
    ].join(" ");
  }

  const start = point(config.startAngle);
  const end = point(config.startAngle + sweep * direction);
  const largeArc = sweep > 180 ? 1 : 0;

  return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArc} ${sweepFlag} ${end.x} ${end.y}`;
};

export class StringCircularText extends StringModule {
  constructor(context: StringContext) {
    super(context);
    this.htmlKey = "circular-text";
    this._type = 2;

    this.cssProperties = [
      { name: "--circular-radius", syntax: "<number>", initialValue: "0", inherits: true },
      { name: "--circular-rotation", syntax: "<number>", initialValue: "0", inherits: true },
      { name: "--circular-sweep", syntax: "<number>", initialValue: "360", inherits: true },
      { name: "--circular-char-total", syntax: "<number>", initialValue: "0", inherits: true },
    ];

    this.permissions.mobile.rebuild.height = false;
    this.permissions.mobile.rebuild.width = false;
    this.permissions.mobile.rebuild.scrollHeight = false;

    this.attributesToMap = [
      ...this.attributesToMap,
      {
        key: "circular-radius",
        type: "dimension",
        fallback: this.settings["circular-radius"] ?? 120,
      },
      {
        key: "circular-start-angle",
        type: "number",
        fallback: this.settings["circular-start-angle"] ?? -90,
      },
      {
        key: "circular-sweep",
        type: "number",
        fallback: this.settings["circular-sweep"] ?? 360,
      },
      {
        key: "circular-direction",
        type: { type: "enum", values: ["cw", "ccw"] },
        fallback: this.settings["circular-direction"] ?? "cw",
      },
      {
        key: "circular-rotate-glyphs",
        type: "boolean",
        fallback: this.settings["circular-rotate-glyphs"] ?? true,
      },
      {
        key: "circular-orientation",
        type: { type: "enum", values: ["radial", "tangent"] },
        fallback: this.settings["circular-orientation"] ?? "tangent",
      },
      {
        key: "circular-inside",
        type: "boolean",
        fallback: this.settings["circular-inside"] ?? false,
      },
      {
        key: "circular-spin-speed",
        type: "number",
        fallback: this.settings["circular-spin-speed"] ?? 0,
      },
      {
        key: "circular-spacing",
        type: "number",
        fallback: this.settings["circular-spacing"] ?? 0,
      },
      {
        key: "circular-keep-upright",
        type: "boolean",
        fallback: this.settings["circular-keep-upright"] ?? false,
      },
      {
        key: "circular-renderer",
        type: { type: "enum", values: ["dom", "svg"] },
        fallback: this.settings["circular-renderer"] ?? "dom",
      },
    ];
  }

  override onInit(): void {
    const rebuild = () => {
      for (let i = 0; i < this.objectsOnPage.length; i++) {
        this.layoutObject(this.objectsOnPage[i], true);
      }
    };

    if ("fonts" in document && document.fonts?.ready) {
      document.fonts.ready.then(rebuild);
      document.fonts.addEventListener?.("loadingdone", rebuild);
    }
  }

  override onObjectConnected(object: StringObject): void {
    super.onObjectConnected(object);
    this.layoutObject(object, true);
  }

  override onResizeWidth(): void {
    super.onResizeWidth();

    for (let i = 0; i < this.objectsOnPage.length; i++) {
      this.layoutObject(this.objectsOnPage[i], true);
    }
  }

  override onFrame(data: StringData): void {
    if (this.objectsOnPage.length === 0) return;

    const dtSeconds = Math.max(1 / 240, Math.min(0.1, (data.time.delta || 16.6667) / 1000));

    for (let i = 0; i < this.objectsOnPage.length; i++) {
      const object = this.objectsOnPage[i];
      const state = getState(object);
      const config = this.getConfig(object);

      if (!state.measured) {
        this.layoutObject(object, true);
        continue;
      }

      // Attributes can change at runtime (devtools, JS, responsive settings).
      // Comparing signatures is what lets the mutate phase stay idle while
      // nothing about the arrangement has actually moved.
      if (buildMeasureKey(config) !== state.appliedMeasureKey) {
        state.needsRemeasure = true;
        state.needsLayout = true;
      } else if (buildLayoutKey(config) !== state.appliedLayoutKey) {
        state.needsLayout = true;
      }

      if (config.spinSpeed !== 0) {
        state.rotation = normalizeRotation(state.rotation + config.spinSpeed * dtSeconds);
        this.applyHostRotation(object.htmlElement, state, false);
      }
    }
  }

  override onMutate(_data: StringData): void {
    for (let i = 0; i < this.objectsOnPage.length; i++) {
      const object = this.objectsOnPage[i];
      const state = getState(object);
      if (!state.needsLayout) continue;

      this.layoutObject(object, state.needsRemeasure);
    }
  }

  private getConfig(object: StringObject): CircularConfig {
    return {
      radius: Math.max(1, object.getProperty<number>("circular-radius") ?? 120),
      startAngle: object.getProperty<number>("circular-start-angle") ?? -90,
      sweep: object.getProperty<number>("circular-sweep") ?? 360,
      direction: object.getProperty<string>("circular-direction") === "ccw" ? -1 : 1,
      rotateGlyphs: object.getProperty<boolean>("circular-rotate-glyphs") !== false,
      orientation:
        (object.getProperty<string>("circular-orientation") ?? "tangent") === "radial"
          ? "radial"
          : "tangent",
      inside: object.getProperty<boolean>("circular-inside") === true,
      spinSpeed: object.getProperty<number>("circular-spin-speed") ?? 0,
      spacing: object.getProperty<number>("circular-spacing") ?? 0,
      keepUpright: object.getProperty<boolean>("circular-keep-upright") === true,
      renderer: object.getProperty<string>("circular-renderer") === "svg" ? "svg" : "dom",
    };
  }

  private layoutObject(object: StringObject, remeasure: boolean): void {
    const state = getState(object);
    const config = this.getConfig(object);

    if (remeasure) {
      this.measureLayout(object, state, config);
    }

    if (!state.measured) return;

    this.applyLayout(object, state, config, true);

    state.appliedMeasureKey = buildMeasureKey(config);
    state.appliedLayoutKey = buildLayoutKey(config);
    state.needsRemeasure = false;
    state.needsLayout = false;
  }

  private measureLayout(object: StringObject, state: CircularState, config: CircularConfig): void {
    const host = object.htmlElement;
    const chars = Array.from(host.querySelectorAll(".-s-char")) as HTMLElement[];

    state.chars = chars;
    state.widths = [];
    state.heights = [];
    state.outwardOffsets = [];
    state.centers = [];
    state.pathLength = 0;
    state.hostSize = 0;
    state.measured = chars.length > 0;

    if (chars.length === 0) {
      return;
    }

    for (let i = 0; i < chars.length; i++) {
      getGlyphElement(chars[i]);
      const metrics = measureGlyph(chars[i]);
      state.widths.push(metrics.width);
      state.heights.push(metrics.height);
      state.outwardOffsets.push(metrics.outwardOffset);
    }

    state.centers[0] = 0;

    for (let i = 1; i < chars.length; i++) {
      const prevWidth = state.widths[i - 1] ?? 0;
      const currentWidth = state.widths[i] ?? 0;

      state.centers[i] = state.centers[i - 1] + prevWidth / 2 + config.spacing + currentWidth / 2;
    }

    const isClosedLoop = Math.abs(Math.abs(config.sweep) - 360) < 0.001;

    if (chars.length <= 1) {
      state.pathLength = 1;
    } else if (isClosedLoop) {
      const lastCenter = state.centers[chars.length - 1] ?? 0;
      const lastWidth = state.widths[chars.length - 1] ?? 0;
      const firstWidth = state.widths[0] ?? 0;
      state.pathLength = Math.max(1, lastCenter + lastWidth / 2 + config.spacing + firstWidth / 2);
    } else {
      const lastCenter = state.centers[chars.length - 1] ?? 0;
      const lastHalf = (state.widths[chars.length - 1] ?? 0) / 2;
      const firstHalf = (state.widths[0] ?? 0) / 2;
      state.pathLength = Math.max(1, lastCenter + lastHalf - firstHalf);
    }

    let farthestDistance = config.radius;
    let maxHalfExtent = 0;

    for (let i = 0; i < chars.length; i++) {
      const width = state.widths[i] ?? 0;
      const height = state.heights[i] ?? 0;
      const outwardOffset = state.outwardOffsets[i] ?? 0;
      const useBaselineShift = config.rotateGlyphs && config.orientation === "radial";
      const radialDistance =
        config.radius + (useBaselineShift ? (config.inside ? -outwardOffset : outwardOffset) : 0);

      farthestDistance = Math.max(farthestDistance, Math.abs(radialDistance));
      maxHalfExtent = Math.max(maxHalfExtent, Math.max(width, height) / 2);
    }

    state.hostSize = Math.ceil((farthestDistance + maxHalfExtent + 8) * 2);
  }

  private applyLayout(
    object: StringObject,
    state: CircularState,
    config: CircularConfig,
    force: boolean,
  ): void {
    const host = object.htmlElement;
    const total = state.chars.length;

    styleTxn.run(() => {
      styleTxn.setProp(host, "position", "relative");
      styleTxn.setProp(host, "display", "inline-block");
      styleTxn.setProp(host, "overflow", "visible");
      styleTxn.setProp(host, "width", `${state.hostSize}px`);
      styleTxn.setProp(host, "height", `${state.hostSize}px`);
      styleTxn.setProp(host, "transformOrigin", "center center");

      styleTxn.setVar(host, "--circular-radius", round3(config.radius));
      styleTxn.setVar(host, "--circular-sweep", round3(config.sweep));
      styleTxn.setVar(host, "--circular-char-total", total);
      this.applyHostRotation(host, state, force);

      if (this.shouldUseSvgRenderer(config)) {
        this.applySvgLayout(host, state, config);
        return;
      }

      this.removeSvgRenderer(state);

      for (let index = 0; index < total; index++) {
        const char = state.chars[index];
        const center = state.centers[index] ?? 0;
        const width = state.widths[index] ?? 0;
        const height = state.heights[index] ?? 0;
        const outwardOffset = state.outwardOffsets[index] ?? 0;

        const angle =
          total <= 1
            ? config.startAngle
            : config.startAngle +
              (center / state.pathLength) * config.sweep * config.direction;

        const radialRotation = angle + (config.inside ? 180 : 0);
        // Tangent glyphs need a quarter-turn relative to the radius so the text
        // follows the path instead of pointing toward/away from the center.
        const tangentBase = config.direction === 1 ? 90 : -90;
        const tangentRotation = angle + tangentBase + (config.inside ? 180 : 0);

        let finalGlyphRotation = 0;

        if (config.rotateGlyphs) {
          finalGlyphRotation = config.orientation === "tangent" ? tangentRotation : radialRotation;
        }

        if (config.rotateGlyphs && config.keepUpright) {
          const normalized = normalizeAngle(finalGlyphRotation);

          if (normalized > 90 || normalized < -90) {
            finalGlyphRotation += 180;
          }
        }

        const useBaselineShift = config.rotateGlyphs && config.orientation === "radial";
        const radialDistance =
          config.radius + (useBaselineShift ? (config.inside ? -outwardOffset : outwardOffset) : 0);
        const radians = degToRad(angle);
        const centerX = state.hostSize / 2 + Math.cos(radians) * radialDistance;
        const centerY = state.hostSize / 2 + Math.sin(radians) * radialDistance;

        const glyph = getGlyphElement(char);
        const anchorTransform = "translate(-50%, -50%)";
        const glyphTransform = `rotate(${round3(config.rotateGlyphs ? finalGlyphRotation : 0)}deg)`;

        styleTxn.setProp(char, "visibility", "visible");
        styleTxn.setProp(char, "pointerEvents", "");
        styleTxn.setProp(char, "position", "absolute");
        styleTxn.setProp(char, "left", `${round3(centerX)}px`);
        styleTxn.setProp(char, "top", `${round3(centerY)}px`);
        styleTxn.setProp(char, "display", "inline-flex");
        styleTxn.setProp(char, "alignItems", "center");
        styleTxn.setProp(char, "justifyContent", "center");
        styleTxn.setProp(char, "width", `${round3(width)}px`);
        styleTxn.setProp(char, "height", `${round3(height)}px`);
        styleTxn.setProp(char, "boxSizing", "border-box");
        styleTxn.setProp(char, "marginRight", "0px");
        styleTxn.setProp(char, "marginLeft", "0px");
        styleTxn.setProp(char, "whiteSpace", "pre");
        styleTxn.setProp(char, "transformOrigin", "center center");
        styleTxn.setProp(char, "transform", anchorTransform);

        styleTxn.setProp(glyph, "display", "inline-flex");
        styleTxn.setProp(glyph, "alignItems", "center");
        styleTxn.setProp(glyph, "justifyContent", "center");
        styleTxn.setProp(glyph, "width", "100%");
        styleTxn.setProp(glyph, "height", "100%");
        styleTxn.setProp(glyph, "whiteSpace", "pre");
        styleTxn.setProp(glyph, "transformOrigin", "center center");
        styleTxn.setProp(glyph, "transform", glyphTransform);

        styleTxn.setVar(char, "--circular-char-angle", round3(angle));
        styleTxn.setVar(
          char,
          "--circular-char-rotation",
          round3(config.rotateGlyphs ? finalGlyphRotation : 0),
        );
        styleTxn.setVar(char, "--circular-char-index", index);
        styleTxn.setVar(char, "--circular-char-width", round3(width));
        styleTxn.setVar(char, "--circular-char-height", round3(height));
        styleTxn.setVar(char, "--circular-char-radius", round3(radialDistance));
      }
    });
  }

  private shouldUseSvgRenderer(config: CircularConfig): boolean {
    return (
      config.renderer === "svg" &&
      config.orientation === "tangent" &&
      config.rotateGlyphs &&
      !config.inside
    );
  }

  private ensureSvgRenderer(host: HTMLElement, state: CircularState): void {
    if (state.svg && state.svgPath && state.svgText && state.svgTextPath) return;

    const svg = document.createElementNS(SVG_NS, "svg");
    const defs = document.createElementNS(SVG_NS, "defs");
    const path = document.createElementNS(SVG_NS, "path");
    const text = document.createElementNS(SVG_NS, "text");
    const textPath = document.createElementNS(SVG_NS, "textPath");

    path.setAttribute("id", state.svgPathId);
    textPath.setAttribute("href", `#${state.svgPathId}`);
    textPath.setAttributeNS("http://www.w3.org/1999/xlink", "xlink:href", `#${state.svgPathId}`);
    textPath.setAttribute("startOffset", "0");

    text.appendChild(textPath);
    defs.appendChild(path);
    svg.appendChild(defs);
    svg.appendChild(text);
    host.appendChild(svg);

    state.svg = svg;
    state.svgPath = path;
    state.svgText = text;
    state.svgTextPath = textPath;
  }

  private removeSvgRenderer(state: CircularState): void {
    state.svg?.remove();
    state.svg = null;
    state.svgPath = null;
    state.svgText = null;
    state.svgTextPath = null;
  }

  private applySvgLayout(host: HTMLElement, state: CircularState, config: CircularConfig): void {
    this.ensureSvgRenderer(host, state);
    const svg = state.svg;
    const path = state.svgPath;
    const text = state.svgText;
    const textPath = state.svgTextPath;
    if (!svg || !path || !text || !textPath) return;

    const textValue = getOriginalText(host);
    const hostStyle = getComputedStyle(host);
    const size = state.hostSize;
    const radius = config.radius;
    const cx = size / 2;
    const cy = size / 2;
    const arcLength = Math.max(1, 2 * Math.PI * radius * (Math.abs(config.sweep) / 360));

    styleTxn.setProp(svg, "position", "absolute");
    styleTxn.setProp(svg, "inset", "0");
    styleTxn.setProp(svg, "width", "100%");
    styleTxn.setProp(svg, "height", "100%");
    styleTxn.setProp(svg, "overflow", "visible");
    styleTxn.setProp(svg, "pointerEvents", "none");
    styleTxn.setProp(text, "fill", "currentColor");
    styleTxn.setProp(text, "fontKerning", "normal");

    svg.setAttribute("viewBox", `0 0 ${size} ${size}`);
    path.setAttribute("d", describeCircularPath(cx, cy, radius, config));
    text.setAttribute("font-family", hostStyle.fontFamily);
    text.setAttribute("font-size", hostStyle.fontSize);
    text.setAttribute("font-weight", hostStyle.fontWeight);
    text.setAttribute("letter-spacing", hostStyle.letterSpacing);
    text.setAttribute("dominant-baseline", "middle");
    textPath.setAttribute("method", "align");
    textPath.setAttribute("spacing", "auto");
    textPath.setAttribute("textLength", String(round3(arcLength)));
    textPath.setAttribute("lengthAdjust", "spacing");
    textPath.textContent = textValue;

    for (let index = 0; index < state.chars.length; index++) {
      const char = state.chars[index];
      styleTxn.setProp(char, "position", "absolute");
      styleTxn.setProp(char, "left", "0px");
      styleTxn.setProp(char, "top", "0px");
      styleTxn.setProp(char, "width", "0px");
      styleTxn.setProp(char, "height", "0px");
      styleTxn.setProp(char, "overflow", "hidden");
      styleTxn.setProp(char, "transform", "none");
      styleTxn.setProp(char, "visibility", "hidden");
      styleTxn.setProp(char, "pointerEvents", "none");
    }
  }

  private applyHostRotation(host: HTMLElement, state: CircularState, force: boolean): void {
    const rotation = round3(state.rotation);

    if (!force && rotation === state.lastAppliedRotation) {
      return;
    }

    styleTxn.run(() => {
      styleTxn.setProp(host, "transformOrigin", "center center");
      styleTxn.setProp(host, "backfaceVisibility", "hidden");
      styleTxn.setProp(host, "transform", "translateZ(0) rotate(var(--circular-rotation-deg, 0deg))");
      styleTxn.setVar(host, "--circular-rotation", rotation);
      styleTxn.setVar(host, "--circular-rotation-deg", `${rotation}deg`);
    });

    state.lastAppliedRotation = rotation;
  }
}
